import { v4 as uuidv4 } from 'uuid';
import { TOOLS } from '../constants/drawingTools';
import { calculateTotalAreaWithHoles } from './booleanOperations';

/**
 * Calculates the Euclidean distance between two points.
 */
export const calculateDistance = (x1, y1, x2, y2) => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

/**
 * Converts a HEX color value to an RGB object.
 * Returns null if the hex string is invalid.
 */
export const hexToRgb = (hex) => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Generates a unique ID.
 */
export const generateId = () => `shape-${uuidv4()}`;

/**
 * Removes duplicate shapes based on their ID
 * Keeps the last occurrence of each shape ID
 * @param {Array} shapes - Array of shape objects
 * @returns {Array} - Array with unique shapes
 */
export const deduplicateShapes = (shapes) => {
  if (!Array.isArray(shapes)) return [];

  const seenIds = new Set();
  const uniqueShapes = [];

  // Iterate in reverse to keep the last occurrence
  for (let i = shapes.length - 1; i >= 0; i--) {
    const shape = shapes[i];
    if (shape && shape.id && !seenIds.has(shape.id)) {
      seenIds.add(shape.id);
      uniqueShapes.push(shape);
    }
  }

  return uniqueShapes.reverse();
};

/**
 * Calculate the area of a polygon using the Shoelace formula (also known as Surveyor's formula)
 * @param {number[]} points - Array of relative points [x1, y1, x2, y2, ...] relative to shape origin
 * @param {number} shapeX - The shape's absolute X position
 * @param {number} shapeY - The shape's absolute Y position
 * @returns {number} - The absolute area in square pixels
 */
export const calculatePolygonArea = (points, shapeX = 0, shapeY = 0) => {
  if (!points || points.length < 6) return 0; // Need at least 3 vertices (6 coordinates)

  let area = 0;
  const numVertices = points.length / 2;

  // Convert relative points to absolute for calculation
  const absolutePoints = [];
  for (let i = 0; i < points.length; i += 2) {
    absolutePoints.push(shapeX + points[i], shapeY + points[i + 1]);
  }

  // Shoelace formula: Area = 0.5 * |Σ(xi * yi+1 - xi+1 * yi)|
  for (let i = 0; i < numVertices; i++) {
    const j = (i + 1) % numVertices; // Wrap around to first vertex
    const xi = absolutePoints[i * 2];
    const yi = absolutePoints[i * 2 + 1];
    const xj = absolutePoints[j * 2];
    const yj = absolutePoints[j * 2 + 1];

    area += (xi * yj - xj * yi);
  }

  return Math.abs(area) / 2;
};

/**
 * Calculate the centroid (geometric center) of a polygon
 * @param {number[]} points - Array of relative points [x1, y1, x2, y2, ...] relative to shape origin
 * @param {number} shapeX - The shape's absolute X position
 * @param {number} shapeY - The shape's absolute Y position
 * @returns {object} - {x, y} coordinates of the centroid in absolute coordinates
 */
export const calculatePolygonCentroid = (points, shapeX = 0, shapeY = 0) => {
  if (!points || points.length < 6) return { x: shapeX, y: shapeY };

  const numVertices = points.length / 2;
  let cx = 0, cy = 0;
  let area = 0;

  // Convert relative points to absolute for calculation
  const absolutePoints = [];
  for (let i = 0; i < points.length; i += 2) {
    absolutePoints.push(shapeX + points[i], shapeY + points[i + 1]);
  }

  // Calculate area and centroid using the same loop
  for (let i = 0; i < numVertices; i++) {
    const j = (i + 1) % numVertices;
    const xi = absolutePoints[i * 2];
    const yi = absolutePoints[i * 2 + 1];
    const xj = absolutePoints[j * 2];
    const yj = absolutePoints[j * 2 + 1];

    const crossProduct = (xi * yj - xj * yi);
    area += crossProduct;
    cx += (xi + xj) * crossProduct;
    cy += (yi + yj) * crossProduct;
  }

  area = area / 2;
  if (Math.abs(area) < 1e-10) return { x: shapeX, y: shapeY }; // Avoid division by zero

  cx = cx / (6 * area);
  cy = cy / (6 * area);

  return { x: cx, y: cy };
};

/**
 * Converts decimal inches to a fractional string (e.g. 5.375 -> "5 3/8", 0.03125 -> "1/32")
 * Rounded to the nearest denominator (default 16)
 * @param {number} inches - The decimal number of inches
 * @param {number} maxDenominator - The maximum precision denominator (e.g., 16, 8, 4)
 * @returns {string} - The formatted fraction
 */
export const decimalToFractionalInches = (inches, maxDenominator = 16) => {
  const wholeInches = Math.floor(inches);
  const remainder = inches - wholeInches;
  const numerator = Math.round(remainder * maxDenominator);

  if (numerator === 0) {
    return wholeInches > 0 ? `${wholeInches}` : '';
  }
  if (numerator === maxDenominator) {
    return `${wholeInches + 1}`;
  }

  // Find greatest common divisor to simplify the fraction (e.g. 16/32 -> 1/2)
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  const divisor = gcd(numerator, maxDenominator);
  const finalNumerator = numerator / divisor;
  const finalDenominator = maxDenominator / divisor;

  return wholeInches > 0
    ? `${wholeInches} ${finalNumerator}/${finalDenominator}`
    : `${finalNumerator}/${finalDenominator}`;
};

/**
 * Get display measurement with smart unit selection
 * @param {number} pixelDistance - Distance in pixels
 * @param {object} currentScaleProps - Scale properties {ratio, documentUnit}
 * @param {number} dpi - Document DPI
 * @param {object} imageSize - Image dimensions
 * @returns {object} - {value: string, unit: string}
 */
export const getDisplayMeasurement = (pixelDistance, currentScaleProps, dpi, imageSize, overrideSystem = null) => {
  const { ratio: scaleRatio, documentUnit } = currentScaleProps || {};

  // Check for all required values to prevent errors
  if (
    typeof pixelDistance !== 'number' ||
    !scaleRatio || !documentUnit || !dpi
  ) {
    return { value: '0.0', unit: 'N/A' };
  }

  // Step 1: Convert pixels to physical document units.
  // CRITICAL: DPI is ALWAYS Pixels Per INCH.
  const inchesOnDocument = pixelDistance / dpi;

  // Convert to documentUnit (mm or in)
  const measurementOnDocument = documentUnit === 'mm'
    ? inchesOnDocument * 25.4
    : inchesOnDocument;

  // Step 2: Apply scale ratio to get real-world measurement
  let realWorldMeasurement = measurementOnDocument * scaleRatio; // in documentUnit

  // Ensure we have a valid number
  if (typeof realWorldMeasurement !== 'number' || isNaN(realWorldMeasurement)) {
    return { value: '0.0', unit: 'N/A' };
  }

  const documentSystem = documentUnit === 'mm' ? 'metric' : 'imperial';
  const targetSystem = overrideSystem && overrideSystem !== 'auto' ? overrideSystem : documentSystem;

  // Convert measurement if target system differs from document system
  if (documentSystem === 'metric' && targetSystem === 'imperial') {
    realWorldMeasurement = realWorldMeasurement / 25.4; // Convert mm to inches
  } else if (documentSystem === 'imperial' && targetSystem === 'metric') {
    realWorldMeasurement = realWorldMeasurement * 25.4; // Convert inches to mm
  }

  // Step 3: Smart unit selection and formatting based on target system
  if (targetSystem === 'metric') {
    // Already in metric, so it's inherently mm
    const realWorldMeasurementMM = realWorldMeasurement;
    if (realWorldMeasurementMM >= 1000) {
      // >= 1 meters: show in meters
      const meters = realWorldMeasurementMM / 1000;
      const precision = meters >= 10 ? 1 : 2;
      return { value: meters.toFixed(precision), unit: 'm' };
    } else if (realWorldMeasurementMM >= 500) {
      // >= 50 cm: show in centimeters  
      const centimeters = realWorldMeasurementMM / 10;
      const precision = centimeters >= 100 ? 0 : 1;
      return { value: centimeters.toFixed(precision), unit: 'cm' };
    } else {
      // < 50 cm: show in millimeters
      return { value: realWorldMeasurementMM.toFixed(0), unit: 'mm' };
    }
  } else if (targetSystem === 'imperial') {
    // Imperial system
    // Round to nearest 1/16 of an inch first to avoid formatting edge cases
    const roundedInches = Math.round(realWorldMeasurement * 16) / 16;

    if (roundedInches >= 12) {
      const feet = Math.floor(roundedInches / 12);
      const remainingInches = roundedInches % 12;
      const fractionalInches = decimalToFractionalInches(remainingInches, 16);

      if (!fractionalInches) {
        return { value: `${feet}'`, unit: '' };
      } else {
        return { value: `${feet}' ${fractionalInches}"`, unit: '' };
      }
    } else {
      const fractionalInches = decimalToFractionalInches(roundedInches, 16);
      return { value: fractionalInches || '0', unit: '"' };
    }
  }

  // Fallback
  return { value: realWorldMeasurement.toFixed(1), unit: documentUnit === 'in' ? '"' : documentUnit };
};

/**
 * Get display area measurement with smart unit selection
 * @param {number} pixelArea - Area in square pixels
 * @param {object} currentScaleProps - Scale properties {ratio, documentUnit}
 * @param {number} dpi - Document DPI
 * @returns {object} - {value: string, unit: string}
 */
export const getDisplayAreaMeasurement = (pixelArea, currentScaleProps, dpi, overrideSystem = null) => {
  const { ratio: scaleRatio, documentUnit } = currentScaleProps || {};

  // Check for all required values
  if (
    typeof pixelArea !== 'number' ||
    !scaleRatio || !documentUnit || !dpi
  ) {
    return { value: '0.0', unit: 'N/A' };
  }

  // Step 1: Convert square pixels to square document units.
  // CRITICAL: DPI is ALWAYS Pixels Per INCH.
  const sqInchesOnDocument = pixelArea / (dpi * dpi);

  // Convert to square documentUnit (mm² or in²)
  const areaOnDocument = documentUnit === 'mm'
    ? sqInchesOnDocument * (25.4 * 25.4)
    : sqInchesOnDocument;

  // Step 2: Apply scale ratio to get real-world area
  let realWorldArea = areaOnDocument * (scaleRatio * scaleRatio); // Square the ratio for area

  // Ensure we have a valid number
  if (typeof realWorldArea !== 'number' || isNaN(realWorldArea)) {
    return { value: '0.0', unit: 'N/A' };
  }

  const documentSystem = documentUnit === 'mm' ? 'metric' : 'imperial';
  const targetSystem = overrideSystem && overrideSystem !== 'auto' ? overrideSystem : documentSystem;

  // Convert area if target system differs from document system
  if (documentSystem === 'metric' && targetSystem === 'imperial') {
    // 1 inch = 25.4 mm => 1 sq inch = 25.4^2 sq mm
    realWorldArea = realWorldArea / (25.4 * 25.4);
  } else if (documentSystem === 'imperial' && targetSystem === 'metric') {
    realWorldArea = realWorldArea * (25.4 * 25.4);
  }

  // Step 3: Smart unit selection based on measurement system
  if (targetSystem === 'metric') {
    // Already in metric, so it's inherently square mm
    const realWorldAreaSqMM = realWorldArea;

    if (realWorldAreaSqMM >= 1000000) {
      // >= 1 square meter: show in square meters
      const sqMeters = realWorldAreaSqMM / 1000000;
      const precision = sqMeters >= 100 ? 0 : sqMeters >= 10 ? 1 : 2;
      return { value: sqMeters.toFixed(precision), unit: 'm²' };
    } else if (realWorldAreaSqMM >= 100) {
      // >= 1 square centimeter: show in square centimeters
      const sqCentimeters = realWorldAreaSqMM / 100;
      const precision = sqCentimeters >= 1000 ? 0 : sqCentimeters >= 100 ? 1 : 2;
      return { value: sqCentimeters.toFixed(precision), unit: 'cm²' };
    } else {
      // < 1 square centimeter: show in square millimeters
      return { value: realWorldAreaSqMM.toFixed(0), unit: 'mm²' };
    }
  } else if (targetSystem === 'imperial') {
    // Imperial system
    if (realWorldArea >= 144) {
      // >= 1 square foot: show in square feet
      const sqFeet = realWorldArea / 144;
      const precision = sqFeet >= 1000 ? 0 : sqFeet >= 100 ? 1 : 2;
      return { value: sqFeet.toFixed(precision), unit: 'ft²' };
    } else {
      // < 1 square foot: show in square inches
      const precision = realWorldArea >= 100 ? 0 : realWorldArea >= 10 ? 1 : 2;
      return { value: realWorldArea.toFixed(precision), unit: 'in²' };
    }
  }

  // Fallback
  return { value: realWorldArea.toFixed(2), unit: `${documentUnit}²` };
};

// Enhanced orthogonal constraint helper function
export const applyOrthogonalConstraint = (currentPos, referencePos, isOrthogonalActive) => {
  if (!isOrthogonalActive || !referencePos || !currentPos) {
    return currentPos;
  }

  const dx = Math.abs(currentPos.x - referencePos.x);
  const dy = Math.abs(currentPos.y - referencePos.y);

  // Snap to the axis with greater movement
  if (dx > dy) {
    return { x: currentPos.x, y: referencePos.y }; // Horizontal constraint
  } else {
    return { x: referencePos.x, y: currentPos.y }; // Vertical constraint
  }
};

// Get reference point for orthogonal constraint based on tool and context
export const getOrthogonalReferencePoint = (selectedTool, currentShape, vertexEditInfo, shapes) => {
  // For vertex editing
  if (vertexEditInfo) {
    const shape = shapes.find(s => s.id === vertexEditInfo.shapeId);
    if (!shape) return null;

    const { vertexIndex, isHole = false, holeIndex = 0 } = vertexEditInfo;
    let points;

    if (isHole && shape.holes && shape.holes[holeIndex]) {
      points = shape.holes[holeIndex];
    } else {
      points = shape.points;
    }

    // For vertex editing, use the previous vertex as reference
    // If editing first vertex, use the last vertex (for closed shapes) or next vertex
    let refIndex;
    if (vertexIndex === 0) {
      // For first vertex, use last vertex if it's a closed shape, otherwise use next vertex
      if (shape.type === TOOLS.AREA) {
        refIndex = (points.length / 2) - 1; // Last vertex for closed shapes
      } else {
        refIndex = 1; // Next vertex for open shapes
      }
    } else {
      refIndex = vertexIndex - 1; // Previous vertex
    }

    if (refIndex >= 0 && refIndex < points.length / 2) {
      return {
        x: shape.x + points[refIndex * 2],
        y: shape.y + points[refIndex * 2 + 1]
      };
    }
    return null;
  }

  // For drawing tools
  switch (selectedTool) {
    case TOOLS.RECTANGLE:
    case TOOLS.CIRCLE:
      return currentShape ? { x: currentShape.startX, y: currentShape.startY } : null;

    case TOOLS.LINE:
    case TOOLS.MEASURE:
    case TOOLS.COTATION:
    case TOOLS.ARROW:
      if (currentShape && currentShape.points && currentShape.points.length >= 2) {
        return { x: currentShape.points[0], y: currentShape.points[1] };
      }
      return null;

    case TOOLS.POLYLINE:
    case TOOLS.AREA:
      if (currentShape && currentShape.points && currentShape.points.length >= 4) {
        const points = currentShape.points;
        const numPoints = points.length;
        return { x: points[numPoints - 4], y: points[numPoints - 3] };
      }
      return null;

    default:
      return null;
  }
};

/**
 * Calculate measurements for all shape types and enhance the shape with measurement data
 * This is the MAIN function that should be called when creating or updating shapes
 */
export const enhanceShapeWithMeasurements = (shape, currentScaleProps, dpi, imageSize, overrideSystem = null) => {
  if (!shape || !shape.type) return shape;

  const enhanced = { ...shape };
  const effectiveDpi = shape.originalDPI || dpi;

  switch (shape.type) {
    case TOOLS.MEASURE:
    case TOOLS.COTATION: {
      // Calculate distance if not already present
      if (!enhanced.distance && enhanced.points && enhanced.points.length >= 4) {
        enhanced.distance = calculateDistance(
          enhanced.points[0], enhanced.points[1],
          enhanced.points[2], enhanced.points[3]
        );
      }

      // Add display measurement
      if (enhanced.distance) {
        enhanced.displayMeasurement = getDisplayMeasurement(enhanced.distance, currentScaleProps, effectiveDpi, imageSize, overrideSystem);
      }
      break;
    }

    case TOOLS.POLYLINE: {
      // Calculate segment distances if not present
      if (!enhanced.segmentDistances && enhanced.points && enhanced.points.length >= 4) {
        const segmentDistances = [];
        for (let i = 0; i < enhanced.points.length - 2; i += 2) {
          const dist = calculateDistance(
            enhanced.points[i], enhanced.points[i + 1],
            enhanced.points[i + 2], enhanced.points[i + 3]
          );
          segmentDistances.push(dist);
        }
        enhanced.segmentDistances = segmentDistances;
      }

      // Calculate total distance and display measurements
      if (enhanced.segmentDistances && enhanced.segmentDistances.length > 0) {
        enhanced.totalDistance = enhanced.segmentDistances.reduce((sum, dist) => sum + dist, 0);
        enhanced.displayTotalMeasurement = getDisplayMeasurement(enhanced.totalDistance, currentScaleProps, effectiveDpi, imageSize, overrideSystem);

        // Individual segment display measurements
        enhanced.displaySegmentMeasurements = enhanced.segmentDistances.map(dist =>
          getDisplayMeasurement(dist, currentScaleProps, effectiveDpi, imageSize, overrideSystem)
        );
      }
      break;
    }

    case TOOLS.AREA: {
      // Calculate area if not present - use calculateTotalAreaWithHoles to properly account for holes
      if (enhanced.points && enhanced.points.length >= 6) {
        // Use existing area calculation or calculate new with hole support
        const area = enhanced.area || calculateTotalAreaWithHoles(enhanced.points, enhanced.holes);
        enhanced.area = area;
        enhanced.displayAreaMeasurement = getDisplayAreaMeasurement(area, currentScaleProps, effectiveDpi, overrideSystem);

        // Store centroid for reference
        enhanced.centroid = calculatePolygonCentroid(enhanced.points, enhanced.x, enhanced.y);
      }

      // Calculate segment distances for polygon perimeter if not present
      if (!enhanced.segmentDistances && enhanced.points && enhanced.points.length >= 6) {
        const segmentDistances = [];
        const numVertices = enhanced.points.length / 2;

        for (let i = 0; i < numVertices; i++) {
          const nextIndex = (i + 1) % numVertices;
          const dist = calculateDistance(
            enhanced.points[i * 2], enhanced.points[i * 2 + 1],
            enhanced.points[nextIndex * 2], enhanced.points[nextIndex * 2 + 1]
          );
          segmentDistances.push(dist);
        }
        enhanced.segmentDistances = segmentDistances;
      }

      // Calculate total distance (perimeter) and display measurements
      if (enhanced.segmentDistances && enhanced.segmentDistances.length > 0) {
        enhanced.totalDistance = enhanced.segmentDistances.reduce((sum, dist) => sum + dist, 0);
        enhanced.displayTotalMeasurement = getDisplayMeasurement(enhanced.totalDistance, currentScaleProps, effectiveDpi, imageSize, overrideSystem);

        // Individual segment display measurements
        enhanced.displaySegmentMeasurements = enhanced.segmentDistances.map(dist =>
          getDisplayMeasurement(dist, currentScaleProps, effectiveDpi, imageSize, overrideSystem)
        );
      }
      break;
    }

    case TOOLS.LINE:
    case TOOLS.ARROW: {
      // Calculate distance if not already present
      if (!enhanced.distance && enhanced.points && enhanced.points.length >= 4) {
        enhanced.distance = calculateDistance(
          enhanced.points[0], enhanced.points[1],
          enhanced.points[2], enhanced.points[3]
        );
      }
      break;
    }

    // Other shape types don't need measurements
    case TOOLS.RECTANGLE:
    case TOOLS.CIRCLE:
    case TOOLS.POINT:
    case TOOLS.PEN:
    default:
      // No measurements needed for these shapes
      break;
  }

  return enhanced;
};

/**
 * Recalculates measurements for a shape after geometry changes
 * This is used when vertices are moved or shapes are transformed
 */
export const recalculateShapeMeasurements = (shape, currentScaleProps, dpi, imageSize, overrideSystem = null) => {
  if (!shape || !shape.type) return shape;

  // Clear existing measurements first
  const cleanShape = { ...shape };
  delete cleanShape.displayMeasurement;
  delete cleanShape.displayTotalMeasurement;
  delete cleanShape.displayAreaMeasurement;
  delete cleanShape.displaySegmentMeasurements;
  delete cleanShape.segmentDistances;
  delete cleanShape.totalDistance;
  delete cleanShape.distance;
  delete cleanShape.area;
  delete cleanShape.centroid;
  delete cleanShape._measurementCacheKey;

  // Recalculate fresh measurements
  return enhanceShapeWithMeasurements(cleanShape, currentScaleProps, dpi, imageSize, overrideSystem);
};

/**
 * Recursively removes properties with undefined values from an object.
 * @param {any} obj The object or value to clean.
 * @returns {any} The cleaned object or value.
 */
export const cleanUndefined = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => cleanUndefined(item)).filter(item => item !== undefined);
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) {
        const cleanedValue = cleanUndefined(value);
        if (cleanedValue !== undefined) {
          newObj[key] = cleanedValue;
        }
      }
    }
  }
  return newObj;
};

// --- Snapping and Intersection Utilities ---

export const SNAP_THRESHOLD = 20; // Screen pixels for snapping sensitivity (zoom-independent UI distance)
export const SNAP_VISIBILITY_THRESHOLD = 50; // Visibility radius in canvas pixels (stage coordinates, zoom-independent)

/**
 * Calculates a "smart" grid spacing that looks good at the current zoom level
 * and respects the document's scale.
 * 
 * @param {object} currentScaleProps - {ratio, documentUnit}
 * @param {number} dpi - Document DPI
 * @param {number} stageScale - Current zoom level
 * @param {string} measurementSystem - 'metric' or 'imperial'
 * @returns {number} - Grid spacing in canvas pixels
 */
export const getSmartGridSpacing = (currentScaleProps, dpi, stageScale, measurementSystem = 'metric') => {
  if (!currentScaleProps || !dpi) return 25; // Fallback

  const { ratio, documentUnit } = currentScaleProps;

  // 1. Calculate how many pixels represent 1 unit of real-world distance in the current system.
  // Metric: base is 1 meter. Imperial: base is 1 foot.
  let baseRealUnitInPixels;
  if (measurementSystem === 'metric') {
    // 1 meter = 1000 mm
    const docUnits = 1000 / ratio; // mm on doc
    const inches = docUnits / (documentUnit === 'mm' ? 25.4 : 1);
    baseRealUnitInPixels = inches * dpi;
  } else {
    // 1 foot = 12 inches
    const docUnits = 12 / ratio; // inches on doc
    const inches = docUnits * (documentUnit === 'mm' ? 1 / 25.4 : 1);
    baseRealUnitInPixels = inches * dpi;
  }

  // 2. We want the grid spacing on the SCREEN to be between min and max pixels.
  // This keeps the grid density comfortable for the eye.
  const targetScreenSpacing = 100;

  const currentUnitOnScreen = baseRealUnitInPixels * stageScale;
  if (currentUnitOnScreen <= 0) return 25;

  // 3. Find a multiplier (power of 10, or 2/5 multiples)
  // We want to find 'm' such that currentUnitOnScreen * m is close to targetScreenSpacing
  const log10 = Math.log10(targetScreenSpacing / currentUnitOnScreen);
  let exponent = Math.floor(log10);
  let fraction = Math.pow(10, log10 - exponent);

  let multiplier;
  if (fraction < 1.5) multiplier = 1;
  else if (fraction < 3.5) multiplier = 2;
  else if (fraction < 7.5) multiplier = 5;
  else {
    multiplier = 1;
    exponent += 1;
  }

  let finalMultiplier = multiplier * Math.pow(10, exponent);

  // 4. LIMIT: Cap the spacing by a maximum real-world value.
  // This prevents the grid from becoming too sparse when zoomed out.
  // For metric, we cap at 10 meters. For imperial, we cap at 20 feet.
  const maxRealInterval = measurementSystem === 'metric' ? 10 : 20;
  if (finalMultiplier > maxRealInterval) {
    finalMultiplier = maxRealInterval;
  }

  return baseRealUnitInPixels * finalMultiplier;
};

// Helper function for snapping logic
// Returns { snappedPoint, visibleSnapPoints }
export const getSnappedPoint = (rawPos, currentDrawingShape, existingShapes, stageCurrentScale, threshold, shapeIdBeingEdited = null, vertexIndexBeingEdited = null, imageSize = { width: 0, height: 0 }, isSnappingEnabled = true, isGridVisible = false, currentScaleProps = null, dpi = 72, measurementSystem = 'metric') => {
  if (!rawPos) return { snappedPoint: rawPos, visibleSnapPoints: [] };

  // If snapping is disabled, return raw position with no visible points
  if (!isSnappingEnabled) {
    return { snappedPoint: rawPos, visibleSnapPoints: [] };
  }

  let closestSnapPoint = null;
  let minSnapDistSq = Math.pow(threshold / stageCurrentScale, 2); // Squared snap distance in stage coordinates (screen pixels adjusted for zoom)
  const visibilityThresholdSq = Math.pow(SNAP_VISIBILITY_THRESHOLD, 2); // Squared visibility distance in stage coordinates (canvas pixels)

  const snapCandidates = [];

  // 1. Add grid points with SMART grid spacing
  if (isGridVisible && imageSize.width > 0 && imageSize.height > 0 && currentScaleProps) {
    const gridSpacing = getSmartGridSpacing(currentScaleProps, dpi, stageCurrentScale, measurementSystem);

    // Round to nearest grid point
    const gx = Math.round(rawPos.x / gridSpacing) * gridSpacing;
    const gy = Math.round(rawPos.y / gridSpacing) * gridSpacing;

    // Add only the nearest grid point to candidates (performance optimization)
    snapCandidates.push({ x: gx, y: gy });
  }

  // 2. Add vertices of the current polyline being drawn (if it has fixed points)
  if (currentDrawingShape && (currentDrawingShape.type === "POLYLINE" || currentDrawingShape.type === "AREA") && currentDrawingShape.points.length >= 2) {
    // Snap to the starting point of the shape being drawn
    snapCandidates.push({ x: currentDrawingShape.points[0], y: currentDrawingShape.points[1] });

    // Snap to other fixed points of the shape being drawn
    if (currentDrawingShape.points.length >= 4) {
      for (let i = 2; i < currentDrawingShape.points.length - 2; i += 2) {
        snapCandidates.push({ x: currentDrawingShape.points[i], y: currentDrawingShape.points[i + 1] });
      }
    }
  }


  // 3. Add vertices from all other existing shapes
  existingShapes.forEach(shape => {
    // Don't snap to the shape being edited
    if (shape.id === shapeIdBeingEdited) return;
    // Don't snap to the shape currently being drawn
    if (currentDrawingShape && shape.id === currentDrawingShape.id) return;

    if (shape.points && shape.points.length > 0) {
      // The shape's points are relative to its x/y origin. Convert them to absolute stage coordinates.
      for (let i = 0; i < shape.points.length; i += 2) {
        snapCandidates.push({
          x: shape.x + shape.points[i],
          y: shape.y + shape.points[i + 1]
        });
      }

      // Also snap to hole vertices
      if (shape.holes && Array.isArray(shape.holes)) {
        shape.holes.forEach(hole => {
          for (let i = 0; i < hole.length; i += 2) {
            snapCandidates.push({
              x: shape.x + hole[i],
              y: shape.y + hole[i + 1]
            });
          }
        });
      }
    } else if (shape.type === "RECTANGLE" || shape.type === "CIRCLE" || shape.type === "POINT") {
      // For shapes with a single x/y origin
      snapCandidates.push({ x: shape.x, y: shape.y });
    }
  });

  const visibleSnapPoints = [];

  for (const sp of snapCandidates) {
    const dx = rawPos.x - sp.x;
    const dy = rawPos.y - sp.y;
    const distSq = dx * dx + dy * dy;

    // Check if this point is within visibility range
    if (distSq < visibilityThresholdSq) {
      visibleSnapPoints.push(sp);
    }

    // Check if this is the closest snap point for actual snapping
    if (distSq < minSnapDistSq) {
      minSnapDistSq = distSq;
      closestSnapPoint = sp;
    }
  }

  return {
    snappedPoint: closestSnapPoint || rawPos,
    visibleSnapPoints
  };
};


// Given three collinear points p, q, r, the function checks if
// point q lies on line segment 'pr'
export function onSegment(p, q, r) {
  return (q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y));
}

// To find orientation of ordered triplet (p, q, r).
export function orientation(p, q, r) {
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  if (val === 0) return 0;  // collinear
  return (val > 0) ? 1 : 2; // clock or counterclock wise
}

// The main function that returns true if line segment 'p1q1'
// and 'p2q2' intersect.
export function doLineSegmentsIntersect(p1, q1, p2, q2) {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;

  return false;
}

/**
 * Resolve the effective scale props for a shape.
 * Uses shape.scaleKey if set, otherwise falls back to page scale.
 * @param {object} shape - The shape object (may have scaleKey field)
 * @param {object} pageScaleProps - The page's current scale props {key, ratio, documentUnit}
 * @param {Array} allScales - Array of all available scales (built-in + custom)
 * @returns {object} The resolved scale props
 */
export const resolveShapeScaleProps = (shape, pageScaleProps, allScales) => {
  if (!shape || !shape.scaleKey) return pageScaleProps;
  const shapeScale = allScales.find(s => s.key === shape.scaleKey);
  return shapeScale || pageScaleProps;
};

export function getShapeBounds(shape) {
  if (!shape) return null;
  const { x = 0, y = 0, points, width = 0, height = 0, radius = 0 } = shape;

  let minX, minY, maxX, maxY;

  switch (shape.type) {
    case TOOLS.RECTANGLE:
    case TOOLS.TEXT:
      return { x, y, width, height };

    case TOOLS.CIRCLE:
    case TOOLS.POINT:
      return { x: x - radius, y: y - radius, width: radius * 2, height: radius * 2 };

    case TOOLS.LINE:
    case TOOLS.MEASURE:
    case TOOLS.COTATION:
    case TOOLS.ARROW: {
      if (!points || points.length < 4) return { x, y, width: 1, height: 1 };
      minX = Math.min(points[0], points[2]);
      minY = Math.min(points[1], points[3]);
      maxX = Math.max(points[0], points[2]);
      maxY = Math.max(points[1], points[3]);
      return { x: x + minX, y: y + minY, width: maxX - minX || 1, height: maxY - minY || 1 };
    }

    case TOOLS.POLYLINE:
    case TOOLS.AREA:
    case TOOLS.PEN: {
      if (!points || points.length < 2) return { x, y, width: 1, height: 1 };
      minX = Infinity; minY = Infinity; maxX = -Infinity; maxY = -Infinity;
      for (let i = 0; i < points.length; i += 2) {
        minX = Math.min(minX, points[i]);
        minY = Math.min(minY, points[i + 1]);
        maxX = Math.max(maxX, points[i]);
        maxY = Math.max(maxY, points[i + 1]);
      }
      return { x: x + minX, y: y + minY, width: maxX - minX || 1, height: maxY - minY || 1 };
    }

    default:
      return { x, y, width: width || 1, height: height || 1 };
  }
}

export function lineSegmentIntersectsRect(p1, p2, rect) {
  if (p1.x >= rect.x && p1.x <= rect.x + rect.width && p1.y >= rect.y && p1.y <= rect.y + rect.height) return true;
  if (p2.x >= rect.x && p2.x <= rect.x + rect.width && p2.y >= rect.y && p2.y <= rect.y + rect.height) return true;

  const r1 = { x: rect.x, y: rect.y };
  const r2 = { x: rect.x + rect.width, y: rect.y };
  const r3 = { x: rect.x + rect.width, y: rect.y + rect.height };
  const r4 = { x: rect.x, y: rect.y + rect.height };

  if (doLineSegmentsIntersect(p1, p2, r1, r2)) return true;
  if (doLineSegmentsIntersect(p1, p2, r2, r3)) return true;
  if (doLineSegmentsIntersect(p1, p2, r3, r4)) return true;
  if (doLineSegmentsIntersect(p1, p2, r4, r1)) return true;

  return false;
}